/**
 * /operations/attendance/overtime-approvals — Session 3B PR 2.
 *
 * Owning agent: Tashghil. AI surfaces: AiBriefPanel (right rail) +
 * InlineAISuggestion (Article 105 ceiling watch) + TraceableValue
 * (Article 107 1.5× premium derivation).
 */

import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Plus, XCircle } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { InlineAISuggestion, PageHeader, TraceableValue } from "../components/ui";
import { useLegalEntityGate } from "../lib/legalEntity";
import { session3bApi } from "../lib/session3b-api";
import type { OvertimeApprovalItem } from "../lib/session3b-api";
import { AiBriefPanel } from "../shell/AiBriefPanel";
import { showAlertTrace } from "./_session3aHelpers";

type StateFilter = "pending" | "approved" | "denied" | "all";

function _stateToneClass(s: OvertimeApprovalItem["state"]): string {
  if (s === "approved") return "bg-emerald-100 text-emerald-800";
  if (s === "denied") return "bg-rose-100 text-rose-800";
  return "bg-amber-100 text-amber-800";
}

function _fmtSar(n: number): string {
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function AttendanceOvertimeApprovalsPage() {
  const { t } = useTranslation();
  const { legalEntityId, gate } = useLegalEntityGate();
  const [stateFilter, setStateFilter] = useState<StateFilter>("pending");

  const list = useQuery({
    queryKey: ["session3b-ot-approvals", legalEntityId, stateFilter],
    queryFn: () =>
      session3bApi.listOvertimeApprovals(
        legalEntityId ?? "",
        stateFilter === "all" ? undefined : stateFilter,
      ),
    enabled: !!legalEntityId,
  });

  const items: OvertimeApprovalItem[] = list.data?.items ?? [];

  const counts = useMemo(() => {
    const sumHours = items.reduce((a, r) => a + r.overtime_hours, 0);
    const sumAmount = items.reduce((a, r) => a + r.article_107_premium_sar, 0);
    const pending = items.filter((r) => r.state === "pending").length;
    // Per-employee hours aggregation — flag any employee approaching 60h/month.
    const perEmp = new Map<string, number>();
    for (const r of items) {
      perEmp.set(r.employee_id, (perEmp.get(r.employee_id) ?? 0) + r.overtime_hours);
    }
    const heaviest = Array.from(perEmp.entries()).sort((a, b) => b[1] - a[1])[0];
    return {
      total: items.length,
      attention: pending,
      critical: heaviest && heaviest[1] >= 60 ? 1 : 0,
      sumHours,
      sumAmount,
      heaviest_employee_id: heaviest?.[0],
      heaviest_hours: heaviest?.[1] ?? 0,
    };
  }, [items]);

  if (gate) return gate;

  return (
    <div className="grid grid-cols-[1fr_280px] gap-4">
      <div className="space-y-3">
        <PageHeader
          title={t("attendance.overtime_approvals.title", "Overtime Approvals")}
          subtitle={t(
            "attendance.overtime_approvals.subtitle",
            "Pre-approval queue for overtime requests with Article 107 premium preview. Tashghil tracks payroll impact; Mutaba'aa watches Article 105 hours ceilings.",
          )}
        />

        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap gap-1.5">
            {(["pending", "approved", "denied", "all"] as StateFilter[]).map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setStateFilter(f)}
                className={`rounded-full border px-3 py-1 text-xs font-medium ${
                  stateFilter === f
                    ? "border-brand-blue bg-brand-blue/10 text-brand-deep"
                    : "border-border-subtle text-text-secondary hover:bg-surface-sunken"
                }`}
              >
                {t(`attendance.overtime_approvals.filter.${f}`, f)}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() =>
              showAlertTrace(
                t(
                  "attendance.overtime_approvals.cta.approve_on_behalf_note",
                  "HR-initiated OT entry — drawer wires to the M30 HR-initiated OT path in a follow-up.",
                ),
              )
            }
            className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-[#2260C9] to-[#A846BF] px-3 py-1.5 text-sm font-medium text-white hover:opacity-95"
          >
            <Plus className="h-4 w-4" />
            {t("attendance.overtime_approvals.cta.approve_on_behalf", "Approve OT on behalf")}
          </button>
        </div>

        {counts.critical > 0 && counts.heaviest_employee_id && (
          <InlineAISuggestion
            agent="tashghil"
            icon={<AlertTriangle className="h-4 w-4" />}
            severity="critical"
            headline={t(
              "attendance.overtime_approvals.suggestion.ceiling_headline",
              `${counts.heaviest_hours.toFixed(1)} OT hours by one employee — Article 105 working-hours ceiling approaching.`,
            )}
            body={t(
              "attendance.overtime_approvals.suggestion.ceiling_body",
              "Mutaba'aa is watching. Tashghil suggests reviewing this employee's pattern before approving further requests.",
            )}
            primaryAction={{
              label: t("attendance.overtime_approvals.suggestion.view_employee", "View employee"),
              onClick: () =>
                window.location.assign(`/people/employees/${counts.heaviest_employee_id}`),
            }}
            suggestionKey={`ot-ceiling-${counts.heaviest_employee_id}`}
          />
        )}
        {counts.total === 0 && !list.isLoading && (
          <InlineAISuggestion
            agent="tashghil"
            icon={<CheckCircle2 className="h-4 w-4" />}
            severity="info"
            headline={t(
              "attendance.overtime_approvals.suggestion.empty_headline",
              "No overtime requests in this state.",
            )}
            body={t(
              "attendance.overtime_approvals.suggestion.empty_body",
              "Tashghil notes: this is normal for the start of a payroll period. Switch the filter to 'All' to see the historical queue.",
            )}
            primaryAction={{
              label: t("attendance.overtime_approvals.suggestion.view_all", "View all"),
              onClick: () => setStateFilter("all"),
            }}
            suggestionKey="ot-empty"
          />
        )}

        {list.isLoading && <Loading />}
        {list.isError && <ErrorPanel message={(list.error as Error).message} />}

        {items.length > 0 && (
          <div className="overflow-hidden rounded-2xl border border-border-subtle bg-white">
            <table className="w-full text-sm">
              <thead className="bg-surface-sunken">
                <tr className="text-left text-[10px] font-semibold uppercase tracking-wide text-text-secondary">
                  <th className="px-3 py-2">{t("attendance.overtime_approvals.col.employee", "Employee")}</th>
                  <th className="px-3 py-2">{t("attendance.overtime_approvals.col.date", "Date")}</th>
                  <th className="px-3 py-2">{t("attendance.overtime_approvals.col.hours", "OT hours")}</th>
                  <th className="px-3 py-2">{t("attendance.overtime_approvals.col.premium", "Article 107 premium (SAR)")}</th>
                  <th className="px-3 py-2">{t("attendance.overtime_approvals.col.state", "State")}</th>
                  <th className="px-3 py-2">{t("attendance.overtime_approvals.col.actions", "Actions")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <tr
                    key={row.ot_id}
                    className="group border-t border-border-subtle hover:bg-surface-sunken/40"
                  >
                    <td className="px-3 py-2">
                      <Link
                        to={`/people/employees/${row.employee_id}`}
                        className="font-medium text-[#2260C9] hover:underline"
                      >
                        {row.employee_name_en}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-text-secondary">
                      <span dir="ltr">{row.attendance_date}</span>
                    </td>
                    <td className="px-3 py-2 text-text-secondary">
                      {row.overtime_hours.toFixed(2)}
                    </td>
                    <td className="px-3 py-2">
                      <TraceableValue
                        onTraceClick={() =>
                          showAlertTrace(
                            t(
                              "attendance.overtime_approvals.trace.premium",
                              `Article 107 premium = basic_hourly (${row.basic_hourly_sar.toFixed(4)} SAR/hr) × 1.5 × overtime_hours (${row.overtime_hours.toFixed(2)}) = ${row.article_107_premium_sar.toFixed(2)} SAR. KSA Labor Law Art. 107 mandates 150% of basic hourly for overtime worked.`,
                            ),
                          )
                        }
                      >
                        <span className="font-mono">{_fmtSar(row.article_107_premium_sar)}</span>
                      </TraceableValue>
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-medium ${_stateToneClass(row.state)}`}
                      >
                        {row.state}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      {row.state === "pending" && (
                        <div className="flex items-center gap-1.5 opacity-50 transition-opacity group-hover:opacity-100">
                          <button
                            type="button"
                            onClick={() =>
                              showAlertTrace(
                                t(
                                  "attendance.overtime_approvals.action.approve_note",
                                  "Approve OT — wires to M30 attendance.approve_overtime in a follow-up.",
                                ),
                              )
                            }
                            className="rounded border border-emerald-300 px-1.5 py-0.5 text-[10px] font-medium text-emerald-700 hover:bg-emerald-50"
                          >
                            <CheckCircle2 className="me-1 inline h-3 w-3" />
                            {t("attendance.overtime_approvals.action.approve", "Approve")}
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              showAlertTrace(
                                t(
                                  "attendance.overtime_approvals.action.deny_note",
                                  "Deny OT — prompt for reason, then write workflow_event.",
                                ),
                              )
                            }
                            className="rounded border border-rose-300 px-1.5 py-0.5 text-[10px] font-medium text-rose-700 hover:bg-rose-50"
                          >
                            <XCircle className="me-1 inline h-3 w-3" />
                            {t("attendance.overtime_approvals.action.deny", "Deny")}
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <aside className="rounded-2xl border border-border-subtle bg-white">
        <AiBriefPanel
          agent="tashghil"
          liveContext={{
            total: counts.total,
            attention: counts.attention,
            critical: counts.critical,
            top_kind: t("attendance.overtime_approvals.title", "Overtime Approvals"),
            window_label: `${counts.sumHours.toFixed(1)}h · est SAR ${_fmtSar(counts.sumAmount)}`,
          }}
        />
      </aside>
    </div>
  );
}
